import { useEffect } from "react";

import { ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PromptAnalytics } from "@/components/admin/prompt/PromptAnalytics";
import { PromptRollouts } from "@/components/admin/prompt/PromptRollouts";
import { PromptTemplates } from "@/components/admin/prompt/PromptTemplates";
import { PromptVersions } from "@/components/admin/prompt/PromptVersions";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUserRole } from "@/hooks/useUserRole";


const PromptManagement = () => {
  const { isAdmin, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const activeTab = searchParams.get("tab") || "templates";

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      navigate("/");
    }
  }, [isAdmin, roleLoading, navigate]);

  if (roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Prompt Management</h1>
            <p className="text-muted-foreground">Manage prompt templates, versions, and rollouts</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="versions">Versions</TabsTrigger>
            <TabsTrigger value="rollouts">Rollouts</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="templates">
            <PromptTemplates />
          </TabsContent>

          <TabsContent value="versions">
            <PromptVersions />
          </TabsContent>

          <TabsContent value="rollouts">
            <PromptRollouts />
          </TabsContent>

          <TabsContent value="analytics">
            <PromptAnalytics />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PromptManagement;
